import {
  BaseRecord,
  CrudFilters,
  CrudSorting,
  OpenNotificationParams,
  useInfiniteList,
  useNotification,
} from "@pankod/refine-core";
import { SelectProps } from "antd/lib/select";
import { debounce } from "lodash";
import {
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { extractApiErrorMessage } from "utils/utils";

type DefaultOption = { value: string | number; label?: string };

type UseInfiniteSelectProps<TData extends BaseRecord = BaseRecord> = {
  resource: string;
  optionLabel: keyof TData | string;
  optionValue: keyof TData | string;
  pageSize?: number;
  sort?: CrudSorting;
  initialFilters?: CrudFilters;
  onSearch?: (value: string) => CrudFilters;
  defaultOption?: DefaultOption;
  debounceTime?: number;
  extraOptions?: DefaultOption[];
  onFetchNextPageError?: (error: unknown) => void;
  fetchNextPageErrorNotification?:
  | false
  | OpenNotificationParams
  | ((error: unknown) => OpenNotificationParams);
  errorNotification?:
  | false
  | OpenNotificationParams
  | ((error: unknown) => OpenNotificationParams);
};

type UseInfiniteSelectReturn = {
  selectProps: SelectProps;
};

export const useInfiniteSelect = <TData extends BaseRecord = BaseRecord>({
  resource,
  optionLabel,
  optionValue,
  pageSize = 20,
  sort,
  initialFilters,
  onSearch,
  defaultOption,
  debounceTime = 400,
  extraOptions,
  onFetchNextPageError,
  fetchNextPageErrorNotification,
  errorNotification,
}: UseInfiniteSelectProps<TData>): UseInfiniteSelectReturn => {
  const { open } = useNotification();
  const fetchNextPageErrorCooldownMs = 2500;
  const lastFetchNextPageErrorNotificationAtRef = useRef(0);

  const initialFiltersMemo = useMemo(
    () => initialFilters ?? [],
    [initialFilters],
  );

  const [filters, setFilters] = useState<CrudFilters>(initialFiltersMemo);

  useEffect(() => {
    setFilters(initialFiltersMemo);
  }, [initialFiltersMemo]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteList<TData>({
      resource,
      config: {
        hasPagination: true,
        pagination: { pageSize },
        sort,
        filters,
      },
      errorNotification: errorNotification
    });

  const options = useMemo(() => {
    const map = new Map<string, { value: string; label: string }>();

    const addOptionToMap = (option?: DefaultOption) => {
      if (!option) return;

      const { value: optionValue, label: optionLabelValue } = option;
      if (
        optionValue === undefined ||
        optionValue === null ||
        optionValue === ""
      ) {
        return;
      }

      const valueAsString = String(optionValue);
      if (map.has(valueAsString)) return;

      map.set(valueAsString, {
        value: valueAsString,
        label: optionLabelValue ? String(optionLabelValue) : valueAsString,
      });
    };

    const pages = data && Array.isArray(data?.pages) ? data.pages : [];

    pages.forEach((page) => {
      const items = Array.isArray(page?.data) ? page.data : [];

      items.forEach((item: any) => {
        const value = item?.[optionValue];
        const label = item?.[optionLabel];
        if (value === undefined || value === null || value === "") return;

        const valueAsString = String(value);
        map.set(valueAsString, {
          value: valueAsString,
          label: label ? String(label) : valueAsString,
        });
      });
    });

    addOptionToMap(defaultOption);
    extraOptions?.forEach(addOptionToMap);

    return Array.from(map.values());
  }, [data?.pages, defaultOption, extraOptions, optionLabel, optionValue]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (onSearch) {
          const nextFilters = onSearch(value);
          setFilters(
            Array.isArray(nextFilters) ? nextFilters : initialFiltersMemo,
          );
          return;
        }

        setFilters(
          value
            ? [
              {
                field: optionLabel as string,
                operator: "eq",
                value,
              },
            ]
            : initialFiltersMemo,
        );
      }, debounceTime),
    [debounceTime, initialFiltersMemo, onSearch, optionLabel],
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (!target || !hasNextPage || isFetchingNextPage) return;

      const reachedBottom =
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 16;

      if (!reachedBottom) return;

      fetchNextPage().catch((error) => {
        onFetchNextPageError?.(error);

        if (fetchNextPageErrorNotification === false) return;

        const now = Date.now();
        const elapsed = now - lastFetchNextPageErrorNotificationAtRef.current;

        if (elapsed < fetchNextPageErrorCooldownMs) return;

        const fallbackMessage =
          extractApiErrorMessage(error) || "Erro ao carregar mais itens.";

        const notificationConfig =
          typeof fetchNextPageErrorNotification === "function"
            ? fetchNextPageErrorNotification(error)
            : fetchNextPageErrorNotification;

        const nextNotification: OpenNotificationParams = notificationConfig ?? {
          type: "error",
          message: fallbackMessage,
        };

        open?.(nextNotification);
        lastFetchNextPageErrorNotificationAtRef.current = now;
      });
    },
    [
      fetchNextPage,
      fetchNextPageErrorNotification,
      hasNextPage,
      isFetchingNextPage,
      onFetchNextPageError,
      open,
    ],
  );

  return {
    selectProps: {
      options,
      showSearch: true,
      filterOption: false,
      loading: isLoading || isFetchingNextPage,
      onSearch: debouncedSearch,
      onPopupScroll: handleScroll,
    },
  };
};